import React, { useState } from 'react';
import { Package, ChevronUp, ChevronRight } from 'lucide-react';
import VehiclesTable from './VehiclesTable';
import { isEnSitio } from '../../../utils/vehicleHelpers';

/**
 * Componente colapsable que muestra vehículos de un subsitio
 * 
 * Features:
 * - Header con nombre y contadores por marca
 * - Indicador de vehículos fuera de sitio
 * - Expandible/colapsable
 * - Tabla de vehículos cuando está expandido
 * 
 * @component
 * @param {Object} props
 * @param {string} props.subsitioNombre - Nombre del subsitio
 * @param {Array} props.vehiculos - Vehículos del subsitio
 * @param {string} props.sitioNombre - Nombre del sitio padre
 * @param {Array} props.visibleColumns - Columnas visibles
 */
const SubsitioGroup = ({ subsitioNombre, vehiculos, sitioNombre, visibleColumns }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // Agrupar vehículos por marca
    const porMarca = vehiculos.reduce((acc, v) => {
        const marca = v.Marca;
        if (!acc[marca]) acc[marca] = [];
        acc[marca].push(v);
        return acc;
    }, {});

    // Contadores de ubicación
    const enSitioCount = vehiculos.filter(v => isEnSitio(v)).length;
    const fueraSitioCount = vehiculos.length - enSitioCount;

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden mb-2">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full px-3 py-2 bg-purple-50 hover:bg-purple-100 flex items-start justify-between gap-2 transition-colors text-left"
            >
                {/* Lado izquierdo: ícono + info + badges (en mobile se apilan) */}
                <div className="flex items-start gap-2 min-w-0 flex-1">
                    <Package className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">{subsitioNombre}</p>
                        <p className="text-xs text-gray-600">
                            {vehiculos.length} vehículo(s) • {Object.keys(porMarca).length} marca(s)
                            {fueraSitioCount > 0 && (
                                <span className="ml-1 text-orange-600">
                                    ({fueraSitioCount} fuera de sitio)
                                </span>
                            )}
                        </p>
                        {/* Badges por marca — siempre debajo en mobile, inline en desktop */}
                        <div className="flex flex-wrap gap-1.5 mt-1 sm:mt-1.5">
                            {Object.entries(porMarca).map(([marca, vehs]) => (
                                <div
                                    key={marca}
                                    className="bg-white px-2 py-0.5 rounded-md border border-gray-200 flex items-center gap-1"
                                >
                                    <span className="text-xs font-medium text-gray-700">{marca}</span>
                                    <span className="px-1.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                                        {vehs.length}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Chevron */}
                <div className="shrink-0 mt-0.5">
                    {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                    ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                </div>
            </button>

            {isExpanded && (
                <div className="p-3 bg-white">
                    <VehiclesTable vehiculos={vehiculos} visibleColumns={visibleColumns} />
                </div>
            )}
        </div>
    );
};

export default SubsitioGroup;